/**
 * V2 Pill / Badge — the `.pill` family from design_reference/styles/app.css.
 * Kinds: ok / warn / bad / info / neutral / accent, with an optional
 * leading colored dot. `Badge` is an alias of `Pill` for vocabulary parity.
 */

import type { ReactNode } from 'react';
import { cn } from '../../../lib/utils';

/** Pill color kinds (mirror the `.pill.*` rules). */
export type PillKind = 'ok' | 'warn' | 'bad' | 'info' | 'neutral' | 'accent';

const KIND_CLASS: Record<PillKind, string> = {
  ok: 'text-[var(--ok)] bg-[var(--ok-bg)] border-[color-mix(in_oklab,var(--ok)_26%,transparent)]',
  warn: 'text-[var(--warn)] bg-[var(--warn-bg)] border-[color-mix(in_oklab,var(--warn)_26%,transparent)]',
  bad: 'text-[var(--bad)] bg-[var(--bad-bg)] border-[color-mix(in_oklab,var(--bad)_26%,transparent)]',
  info: 'text-[var(--info)] bg-[var(--info-bg)] border-[color-mix(in_oklab,var(--info)_26%,transparent)]',
  neutral: 'text-[var(--tx-mid)] bg-[var(--bg-3)] border-[var(--bd)]',
  accent: 'text-[var(--accent-ink)] bg-[var(--accent-soft)] border-[var(--accent-line)]',
};

/** Props for the V2 Pill. */
export interface PillProps {
  kind?: PillKind;
  /** Optional dot color (CSS color / token reference). Renders a leading dot. */
  dot?: string;
  children: ReactNode;
  className?: string;
}

/** V2 status pill / badge. */
export function Pill({ kind = 'neutral', dot, children, className }: PillProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-[5px] h-5 px-2 rounded-[var(--r-full)]',
        'text-[11px] font-[550] tracking-[.01em] border whitespace-nowrap',
        '[&_svg]:w-3 [&_svg]:h-3',
        KIND_CLASS[kind],
        className,
      )}
    >
      {dot && (
        <span
          className="w-[6px] h-[6px] rounded-full"
          style={{ background: dot }}
          aria-hidden="true"
        />
      )}
      {children}
    </span>
  );
}

/** Alias of {@link Pill} for badge vocabulary. */
export const Badge = Pill;
export type BadgeProps = PillProps;
